import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import {
  ClientMetadata,
  EventRequest,
  friendlierAnalyticsValue,
  KeyValue,
  NavigationInfoKey,
} from '@rivethealth/data/analytics';
import { EventType } from '@rivethealth/data/analytics/event-types';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { API_URL } from '../../api/common';
import { Initializer } from '../../initialize';
import { getCompleteRoute } from '../state/route.service';
import { pageTitleFromRouteData } from '../state/title.service';

export type AnalyticsEventOutput<T1> = (arg: T1) => void;

@Injectable()
export abstract class AnalyticsService {
  /** Event to fire on page title changes */
  abstract navigationEvent: EventType<any>;

  readonly pageTitleAndRoute: Observable<
    [string | null, ActivatedRouteSnapshot]
  > = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(event => event as NavigationEnd),
    map(event => {
      const title = pageTitleFromRouteData(
        event.url,
        this.router.routerState.snapshot.root,
      );
      const route = getCompleteRoute(this.router);
      const titleAndRoute: [string | null, ActivatedRouteSnapshot] = [
        title,
        route,
      ];
      return titleAndRoute;
    }),
    filter(([title]) => !!title),
    // We want to capture URL changes e.g. from `patients/estimations`
    // to `patients/estimations&action=simple-estimate-view`
    // but not to `patients/estimations?serviceDate=all`. Therefore, this hinges
    // on all notable pages/views having distinct titles in the routes tree.
    distinctUntilChanged(([title1], [title2]) => title1 === title2),
  );

  protected getBrowserLocales(): readonly string[] {
    const locales =
      navigator.languages === undefined
        ? [navigator.language]
        : navigator.languages;

    if (!locales) return [];
    return locales;
  }

  protected getClientMetadata(): ClientMetadata {
    return {
      user_agent: window.navigator.userAgent,
      device_pixel_ratio: window.devicePixelRatio,
      user_viewport_height: window.innerHeight,
      user_viewport_width: window.innerWidth,
      locales: this.getBrowserLocales(),
    };
  }

  constructor(
    @Inject(API_URL) protected readonly apiUrl: string,
    protected readonly http: HttpClient,
    protected readonly router: Router,
  ) {}

  logEvent(type: EventType<undefined>): void;
  logEvent<T>(type: EventType<T>, properties: T): void;
  logEvent<T>(type: EventType<T>, properties?: T): void {
    this.rawEvents.next([type, properties]);
  }

  /**
   * This effectively merges the events coming from the app (logEvent) with the
   * ones coming from navigation (routeAnalyticsInitializer) and maintains
   * the correct order of events. (Async functions tend to lag behind a little,
   * so we avoid those).
   */
  protected readonly rawEvents = new Subject<[EventType<any>, any]>();

  /** This gets used by the implementation of this class and sent to the API */
  protected readonly events: Observable<EventRequest> = this.rawEvents.pipe(
    withLatestFrom(
      this.pageTitleAndRoute.pipe(startWith([null, null]), pairwise()),
    ),
    map(
      ([
        [eventType, properties],
        [[previousTitle, previousRoute], [currentTitle, currentRoute]],
      ]) => {
        const name = eventType.name;

        const navigationInfo: { [key in NavigationInfoKey]: string | null } = {
          'current page name': currentTitle,
          'current page route': this.stringifyParamRoute(currentRoute),
          'current page url': this.stringifyLiteralRoute(currentRoute),
          'previous page name': previousTitle,
          'previous page route': this.stringifyParamRoute(previousRoute),
          'previous page url': this.stringifyLiteralRoute(previousRoute),
        };

        const metadata = Object.entries<KeyValue['value']>({
          ...navigationInfo,
          ...properties,
        }).map(([key, value]) => ({
          key,
          value: friendlierAnalyticsValue(value),
        }));

        return {
          name,
          metadata,
          client_metadata: this.getClientMetadata(),
        };
      },
    ),
  );

  /** E.g. "accounts/:accountId/patients/patients/:patientId/info" */
  stringifyParamRoute(route: ActivatedRouteSnapshot | null): string | null {
    if (!route) return null;
    return route.pathFromRoot
      .map(route => route.routeConfig?.path)
      .filter(segment => !!segment)
      .join('/');
  }

  /** E.g. "accounts/205/patients/patients/1234567/info" */
  stringifyLiteralRoute(route: ActivatedRouteSnapshot | null): string | null {
    if (!route) return null;
    return route.pathFromRoot
      .map(route => route.url)
      .filter(segment => segment.toString() !== '')
      .join('/');
  }
}

export class ViewEventArgumentService<T> {
  args: Observable<T>;
}

export function routeAnalyticsInitializer<T>(
  analyticsService: AnalyticsService,
  router: Router,
  viewEventArgService?: ViewEventArgumentService<T>,
): Initializer {
  // `withLatestFrom` behavior: If the outer Observable emits before the
  // Observable inside `withLatestFrom` emits for the first time, the value gets
  // dropped completely.
  //
  // `concatLatestFrom` behavior: In that situation, the pipeline would wait
  // for the inner Observable to emit, then couple the values together like
  // normal. Only available in NgRx.
  //
  // `combineLatest` + `distinctUntilChanged`: Should act identical to
  // `concatLatestFrom`. Used here since we don't have NgRx installed.
  //
  // `combineLatestWith`: Identical to using `combineLatest` alone.
  //
  // https://stackoverflow.com/questions/72207174/rxjs-any-joining-alternative-to-withlatestfrom-operator
  return combineLatest([
    analyticsService.pageTitleAndRoute,
    viewEventArgService?.args ?? of(null),
  ]).pipe(
    distinctUntilChanged(([[title1]], [[title2]]) => title1 === title2),
    tap(([[title, route], viewEventArgs]) => {
      if (viewEventArgs) {
        analyticsService.logEvent(
          analyticsService.navigationEvent,
          viewEventArgs,
        );
      } else {
        analyticsService.logEvent(analyticsService.navigationEvent);
      }
    }),
  );
}
